import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Medusa from '../services/api';
import { useAdminGetSession } from 'medusa-react';
import { trackFeatureFlags } from '../utils/datadog-rum';

type FeatureFlagsContextType = {
  flags: Record<string, boolean>;
  isFeatureEnabled: (flag: string) => boolean;
  updateFlag: (flag: string, value: boolean) => Promise<void>;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

type FeatureFlagProviderProps = {
  children: React.ReactNode;
  initialFlags?: Record<string, boolean>;
};

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  initialFlags = {},
}) => {
  const { user } = useAdminGetSession();
  const [flags, setFlags] = useState<Record<string, boolean>>(initialFlags);

  const loadFlags = useCallback(async () => {
    try {
      const response = await Medusa.features.list(user?.id);
      const featureFlags = response.data.data.features.reduce(
        (acc: Record<string, boolean>, flag: { key: string; value: boolean }) => ({ ...acc, [flag.key]: flag.value }),
        {} as Record<string, boolean>
      );
      setFlags(featureFlags);
      trackFeatureFlags(featureFlags);
    } catch (error) {
      console.error('Failed to load feature flags:', error);
    }
  }, [user]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const isFeatureEnabled = useCallback((flag: string) => {
    return !!flags[flag];
  }, [flags]);

  const updateFlag = useCallback(async (flag: string, value: boolean) => {
    if (!user?.id) return;
    try {
      await Medusa.features.overrideForUser(flag, user.id, value);
    } catch (error) {
      await loadFlags();
      return;
    }
    await loadFlags();
    trackFeatureFlags(flags);
  }, [user, loadFlags, flags]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isFeatureEnabled, updateFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}; 